import { Link } from "@tanstack/react-router";
import { MoreHorizontal, Trash2, Edit, BookOpen, LogOut, Users } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription } from "@/client/components/ui/card";
import { Button } from "@/client/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/client/components/ui/dropdown-menu";
import type { Story } from "@/client/db";

interface ProjectCardProps {
  story: Story;
  onEdit: (story: Story) => void;
  onDelete: (story: Story) => void;
  onLeave: (story: Story) => void;
}

export function ProjectCard({ story, onEdit, onDelete, onLeave }: ProjectCardProps) {
  const isOwner = story.role === "owner";
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(date));
  };

  return (
    <Card className="group relative hover:border-primary/50 transition-colors">
      <Link
        to="/story/$storyId"
        params={{ storyId: String(story.id) }}
        aria-label={`Open ${story.name}`}
        className="absolute inset-0 z-0 rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <CardHeader className="relative z-10 pointer-events-none">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">{story.name}</CardTitle>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Options for ${story.name}`}
                className="h-8 w-8 pointer-events-auto opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(story)}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {isOwner ? (
                <DropdownMenuItem
                  onClick={() => onDelete(story)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => onLeave(story)}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Leave
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {(story.synopsis || story.description) && (
          <CardDescription className="line-clamp-2 mt-2">
            {story.synopsis || story.description}
          </CardDescription>
        )}
        <p className="text-xs text-muted-foreground mt-4 flex items-center gap-2">
          <span>Updated {formatDate(story.updatedAt)}</span>
          {!isOwner && (
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" />
              Shared by {story.ownerName ?? "another writer"}
            </span>
          )}
        </p>
      </CardHeader>
    </Card>
  );
}
